import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem('access_token');
}

function getStoredRefreshToken() {
  return localStorage.getItem('refresh_token');
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem('access_token', access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
}

function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/auth/me`, {
        headers: getAuthHeaders(),
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, { email, password });
    const data = response.data;
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/api/auth/register`, { email, password, name });
    const data = response.data;
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch (e) {
      // Logout endpoint can 401 if token already expired — not a real error for the user
      console.debug('Logout endpoint returned an error (token may already be expired):', e?.message);
    }
    clearTokens();
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      const rt = getStoredRefreshToken();
      const response = await axios.post(`${API}/api/auth/refresh`, {}, {
        headers: rt ? { 'X-Refresh-Token': rt } : {},
      });
      if (response.data.access_token) {
        storeTokens(response.data.access_token, null);
      }
      await checkAuth();
    } catch (error) {
      clearTokens();
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshToken, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
